import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from './TanTable';
import { useHistoryPanelData } from '../hooks/useHistoryPanelData';
import { formatHistoryDateTime, formatIsoDateOnly } from '../utils/dateUtils';

interface RescheduleRecord {
  recno: number;
  createdatetime: string | null;
  createdby: string;
  servicefailuredate: string | null;
  olddate: string | null;
  newdate: string | null;
  reason: string;
  thd_reason: string;
  responsibledriver: string;
}

interface ReschedulesResponse {
  count: number;
  records: RescheduleRecord[];
}

interface ReschedulesPanelProps {
  pronumber: number | null;
  /** Defer fetching until the panel is actually shown. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
}

export const ReschedulesPanel: React.FC<ReschedulesPanelProps> = ({
  pronumber,
  active = true,
  refreshNonce = 0,
}) => {
  const { data, loading, error } = useHistoryPanelData<ReschedulesResponse>(
    pronumber ? `/orders/${pronumber}/reschedules` : null,
    active,
    refreshNonce,
    'Failed to fetch reschedules'
  );

  const columns = useMemo<ColumnDef<RescheduleRecord>[]>(
    () => [
      {
        accessorKey: 'createdby',
        header: 'User',
        size: 120,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'createdatetime',
        header: 'Create Date',
        size: 160,
        cell: (info) => formatHistoryDateTime(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'servicefailuredate',
        header: 'Service Failure Date',
        size: 130,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'olddate',
        header: 'Old Date',
        size: 100,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'newdate',
        header: 'New Date',
        size: 100,
        cell: (info) => formatIsoDateOnly(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'reason',
        header: 'Reason',
        size: 180,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
      {
        accessorKey: 'thd_reason',
        header: 'THD Reason',
        size: 160,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <div className="truncate" title={v}>
              {v}
            </div>
          );
        },
      },
      {
        accessorKey: 'responsibledriver',
        header: 'Driver',
        size: 110,
        cell: (info) => (info.getValue() as string) || '-',
      },
    ],
    []
  );

  return (
    <div className="h-full flex flex-col">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}
      <div className="flex-1 min-h-[200px]">
        <TanTable
          data={data?.records || []}
          columns={columns}
          loading={loading}
          cardTitle="Reschedules"
          searchPlaceholder="Search reschedules..."
          settingsKey="pro-history-reschedules"
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          defaultSorting={[{ id: 'createdatetime', desc: true }]}
        />
      </div>
    </div>
  );
};

export default ReschedulesPanel;
